import { useState } from 'react'
import { RadioGroup } from '@headlessui/react'
import { cn } from 'cn'
import { useCartUpdateTip } from 'components/pages/CartPage/CartTipSelector/types/cartUpdateTip'
import Button from 'components/ui/Button'
import Notification from 'components/Notification'
import { MoneyField } from 'components/ui/FormMoneyField'

type TipOption = {
  value: number | 'other'
  text: string
}

const tipOptions = [
  {
    value: 10,
    text: '10%'
  },
  {
    value: 15,
    text: '15%'
  },
  {
    value: 20,
    text: '20%'
  },
  {
    value: 0,
    text: 'Zero'
  },
  {
    value: 'other',
    text: 'Other'
  }
]

type Props = {
  uuid: string
  refetch: () => void
  currentAmountCustomTip: number | undefined
  currentTipPercentage: number | null | undefined
  error?: string | null
  resetTipError: () => void
}

export default function CartTipSelector({
  uuid,
  refetch,
  currentAmountCustomTip,
  currentTipPercentage,
  error,
  resetTipError
}: Props) {
  const [customTip, setCustomTip] = useState<number | undefined>(
    currentAmountCustomTip || undefined
  )

  const [customTipVisible, setCustomTipVisible] = useState(!!customTip)

  const [cartUpdateTip] = useCartUpdateTip({})

  const makeCartUpdateTip = async ({
    amountCustomTipCents,
    tipPercentage
  }: {
    amountCustomTipCents?: number | null
    tipPercentage?: number | null
  }) => {
    resetTipError()
    const variables: {
      cartUuid: string
      amountCustomTipCents?: number
      tipPercentage?: number
    } = { cartUuid: uuid }

    if (amountCustomTipCents || amountCustomTipCents === 0) {
      variables.amountCustomTipCents = amountCustomTipCents
    }

    if (tipPercentage || tipPercentage === 0) {
      variables.tipPercentage = tipPercentage
    }

    const response = await cartUpdateTip({
      notifications: {
        onError: true
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      variables: variables as any
    })

    if (!response) return

    await refetch()
  }

  const showCustomTipInput = () => setCustomTipVisible(true)

  const onCartUpdateTip = async (option: TipOption) => {
    resetTipError()
    if (option.value === 'other') {
      await makeCartUpdateTip({ tipPercentage: null })
      showCustomTipInput()
      return
    }
    setCustomTip(undefined)
    setCustomTipVisible(false)
    await makeCartUpdateTip({ tipPercentage: option.value })
  }

  const selectCustomTip = async () => {
    await makeCartUpdateTip({
      amountCustomTipCents: customTip
    })
    await refetch()
  }

  const getCurrentValue = () => {
    const otherOption = tipOptions.find((o) => o.value === 'other')

    if (customTip !== null && typeof customTip !== 'undefined') {
      return otherOption
    }

    if (
      !customTipVisible &&
      !currentTipPercentage &&
      currentTipPercentage !== 0
    ) {
      return null
    }

    return (
      tipOptions.find((o) => o.value === currentTipPercentage) ?? otherOption
    )
  }

  return (
    <div id="tipSelector">
      <div className="flex items-center justify-between">
        <h2 className="block text-sm font-medium text-gray-800">Tip</h2>
      </div>

      <RadioGroup
        className="mt-2"
        // @ts-ignore
        onChange={onCartUpdateTip}
        value={(getCurrentValue() ?? undefined) as TipOption | undefined}
      >
        <RadioGroup.Label className="sr-only">Choose tip</RadioGroup.Label>
        <div className="grid grid-cols-5 gap-3">
          {tipOptions.map((option) => (
            <RadioGroup.Option
              key={option.text}
              className={({ active, checked }) =>
                cn(
                  active ? 'ring-2 ring-offset-2 ring-orange-500' : '',
                  checked
                    ? 'bg-orange-500 border-transparent text-white hover:bg-orange-600'
                    : 'bg-white border-gray-300 text-gray-900 hover:bg-gray-50',
                  'border border-solid rounded-md py-3 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1 cursor-pointer text-center focus:outline-hidden'
                )
              }
              value={option}
            >
              <RadioGroup.Label as="p">{option.text}</RadioGroup.Label>
            </RadioGroup.Option>
          ))}
        </div>
      </RadioGroup>
      {customTipVisible && (
        <div className="mt-4">
          <div className="block text-sm font-medium text-gray-700">
            How much do you want to tip? (E.g. 10.50)
          </div>
          <div className="flex">
            <div className="grow">
              <MoneyField
                currency="$"
                name="amountCustomTipCents"
                onChange={(event) =>
                  setCustomTip(Math.round((event.target.floatValue ?? 0) * 100))
                }
                placeholder="0.00"
                value={customTip ? Number(customTip / 100) : ''}
              />
            </div>
            <div className="ml-2 mt-1">
              <Button
                color="primary"
                disabled={customTip === currentAmountCustomTip}
                onClick={selectCustomTip}
                size="medium"
                variant="outline"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
      {error && (
        <div className="mt-2">
          <Notification variant="error">{error}</Notification>
        </div>
      )}
    </div>
  )
}
